import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Card, Table, message } from "antd";
import type { TableColumnsType } from "antd";

import RemovePopconfirm from "components/remove-popconfirm/remove-popconfirm";
import MediaPlayer from "components/media-player/media-player";
import NewProductModal from "components/new-product-modal/new-product-modal";
import NewInstalledProductModal from "components/new-installed-product-modal/new-installed-product-modal";
import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import StructuredLocation from "components/structured-location/structured-location";

import { getFileIcon } from "utils/files-management";
import { labelsFromRelationships } from "utils/location-hierarchy";
import { MediaType } from "constants/field-values";
import { Handover, Products, Installed_Products, Properties } from "graphql/generated/graphql";
import { useDeleteProductByIdMutation } from "graphql/mutations/delete-product-by-id.generated";
import { useDeleteInstalledProductByIdMutation } from "graphql/mutations/delete-installed-product-by-id.generated";

interface HandoverPublishedDevicesProps {
	handover: Handover;
	readOnly?: boolean;
}

interface FileViewerState {
	type: MediaType | null;
	src: string;
}

interface GroupedProducts {
	product_type: {
		name: string;
		label: string;
	};
	totalProducts: number;
	totalInstalledProducts: number;
	products: Products[];
}

/**
 * Group installed_products by product category.
 * Sub-groups by product.id within each category, producing the GroupedProducts shape
 */
const groupInstalledProductsByCategory = (installedProducts: Installed_Products[]): GroupedProducts[] => {
	const grouped = new Map<string, GroupedProducts>();
	// Tracks the synthetic Products entry per "categoryName-productId" key
	const productEntryMap = new Map<string, Products>();

	installedProducts.forEach(installedProduct => {
		const product = installedProduct.product;
		if (!product) return;

		// 1. Ensure the category group exists
		const categoryName = product.product_category?.name || "UNCATEGORIZED";
		const categoryLabel = product.product_category?.label || "Uncategorized";

		if (!grouped.has(categoryName)) {
			grouped.set(categoryName, {
				product_type: { name: categoryName, label: categoryLabel },
				totalProducts: 0,
				totalInstalledProducts: 0,
				products: []
			});
		}

		const group = grouped.get(categoryName)!;

		// 2. Ensure the product sub-group entry exists within the category
		const productEntryKey = `${categoryName}-${product.id}`;

		if (!productEntryMap.has(productEntryKey)) {
			// Clone the product template and attach a fresh mutable installed_products array
			const productEntry = { ...product, installed_products: [] as Installed_Products[] } as unknown as Products;
			productEntryMap.set(productEntryKey, productEntry);
			group.products.push(productEntry);
			group.totalProducts += 1;
		}

		// 3. Accumulate the installed product into its product entry
		const productEntry = productEntryMap.get(productEntryKey)!;
		(productEntry.installed_products as Installed_Products[]).push(installedProduct);
		group.totalInstalledProducts += 1;
	});

	return Array.from(grouped.values());
};

const HandoverPublishedDevices: React.FC<HandoverPublishedDevicesProps> = ({ handover, readOnly = false }) => {
	const [fileViewer, setFileViewer] = useState<FileViewerState>({ type: null, src: "" });

	// Modal states
	const [productModalOpen, setProductModalOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState<Products | null>(null);
	const [installedProductModalOpen, setInstalledProductModalOpen] = useState(false);
	const [selectedInstalledProduct, setSelectedInstalledProduct] = useState<Installed_Products | null>(null);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState(false);

	// State for expand/collapse
	const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
	const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

	// Group products by category, sourcing from installed_products
	const productsGroupedByCategory = useMemo(() => {
		if (!handover.installed_products || handover.installed_products.length === 0) {
			return [];
		}
		return groupInstalledProductsByCategory(handover.installed_products as Installed_Products[]);
	}, [handover.installed_products]);

	// Initialize all categories and products as expanded
	useEffect(() => {
		if (productsGroupedByCategory && productsGroupedByCategory.length > 0) {
			const categoryStates: Record<string, boolean> = {};
			const productStates: Record<string, boolean> = {};

			productsGroupedByCategory.forEach((category, categoryIndex) => {
				const categoryKey = `${category.product_type?.name}-${categoryIndex}`;
				categoryStates[categoryKey] = true;

				category.products?.forEach((product: Products, productIndex: number) => {
					const productKey = `${categoryKey}-product-${product.id || productIndex}`;
					productStates[productKey] = true;
				});
			});

			setExpandedCategories(categoryStates);
			setExpandedProducts(productStates);
		}
	}, [productsGroupedByCategory]);

	const toggleCategory = (categoryKey: string) => {
		setExpandedCategories(prev => ({
			...prev,
			[categoryKey]: !prev[categoryKey]
		}));
	};

	const toggleProduct = (productKey: string) => {
		setExpandedProducts(prev => ({
			...prev,
			[productKey]: !prev[productKey]
		}));
	};

	// Delete product mutation
	const [deleteProductByIdMutation] = useDeleteProductByIdMutation({
		refetchQueries: ["getHandoverById"],
		onCompleted: () => {
			message.success("Product deleted successfully");
		},
		onError: error => {
			console.error("Error deleting product:", error);
			message.error("Failed to delete product");
		}
	});

	// Delete installed product mutation
	const [deleteInstalledProductByIdMutation] = useDeleteInstalledProductByIdMutation({
		refetchQueries: ["getHandoverById"],
		onCompleted: () => {
			message.success("Installed product deleted successfully");
		},
		onError: error => {
			console.error("Error deleting installed product:", error);
			message.error("Failed to delete installed product");
		}
	});

	const handleOnClickAttachment = useCallback((file: any) => {
		const { type, photo_link, video_link, document_link, audio_link } = file;

		let fileUrl = photo_link || video_link || document_link || audio_link;
		let mediaType: MediaType = MediaType.Document;

		if (type === MediaType.Image || photo_link) {
			mediaType = MediaType.Image;
		} else if (type === MediaType.Video || video_link) {
			mediaType = MediaType.Video;
		} else if (type === MediaType.Audio || audio_link) {
			mediaType = MediaType.Audio;
		}

		setFileViewer({
			type: mediaType,
			src: fileUrl
		});
	}, []);

	const handleOpenProductModal = useCallback((product: Products) => {
		setSelectedProduct(product);
		setProductModalOpen(true);
	}, []);

	const handleCloseProductModal = useCallback(() => {
		setProductModalOpen(false);
		setSelectedProduct(null);
	}, []);

	const handleDeleteProduct = useCallback(
		async (productId: string) => {
			await deleteProductByIdMutation({
				variables: { id: productId }
			});
		},
		[deleteProductByIdMutation]
	);

	const handleOpenInstalledProductModal = useCallback((installedProduct: Installed_Products) => {
		setSelectedInstalledProduct(installedProduct);
		setInstalledProductModalOpen(true);
	}, []);

	const handleCloseInstalledProductModal = useCallback(() => {
		setInstalledProductModalOpen(false);
		setSelectedInstalledProduct(null);
	}, []);

	const handleDeleteInstalledProduct = useCallback(
		async (installedProductId: string) => {
			await deleteInstalledProductByIdMutation({
				variables: { id: installedProductId }
			});
		},
		[deleteInstalledProductByIdMutation]
	);

	const handleOnCloseAttachmentModal = useCallback(() => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
	}, []);

	const updateAttachments = useCallback((attachmentFile: any) => {
		// This will be handled by the modals
	}, []);

	// Table columns configuration for installations table
	const installationsTableColumns: TableColumnsType<any> = useMemo(
		() => [
			{
				title: "IMAGE",
				key: "image",
				width: 75,
				render: (_: any, record: Installed_Products) => {
					let productImgUri = `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`;

					if (record.photo_link) {
						productImgUri = record.photo_link;
					} else {
						if (record.installed_product_attachments?.length) {
							const attachmentPhoto = record.installed_product_attachments.find(pa => pa.photo_link)?.photo_link;
							if (attachmentPhoto) {
								productImgUri = attachmentPhoto;
							}
						}
					}

					return (
						<img
							src={productImgUri}
							alt="Installed device icon"
							className={"device-detection-device-header-img" + (record.photo_link ? "" : " placeholder")}
						/>
					);
				}
			},
			{
				title: "LOCATION",
				dataIndex: "location",
				key: "location",
				render: (location: any, record: any) => (
					<>
						<StructuredLocation labels={labelsFromRelationships(record)} />
						{location || "-"}
					</>
				)
			},
			{
				title: "SERIAL #",
				dataIndex: "product_serial",
				key: "product_serial",
				minWidth: 100,
				render: (serial: any, record: any) => {
					if (!serial || serial === "null" || serial === "not_visible") return "-";
					return serial;
				}
			},
			{
				title: "VISIBLE",
				dataIndex: "installation_visible",
				key: "visible",
				width: 100,
				render: (visible: any) => {
					return visible ? "Yes" : "No";
				}
			},
			// Actions column
			!readOnly
				? {
						title: "",
						key: "actions",
						minWidth: 100,
						render: (_: any, record: Installed_Products) => {
							const handleEditClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleOpenInstalledProductModal(record);
							};
							const handleDeleteClick = (e: React.MouseEvent) => {
								e.stopPropagation();
								handleDeleteInstalledProduct(record.id);
							};

							return (
								<div className="table-actions" key={record.id} onClick={e => e.stopPropagation()}>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className={"selected-file-icon"}
										onClick={handleEditClick}
									/>
									<RemovePopconfirm
										title="Delete Installed Device"
										description={<span>Are you sure you want to delete this installed device?</span>}
										handleDeleteClick={handleDeleteClick}
									/>
								</div>
							);
						}
					}
				: {}
		],
		[readOnly, handleOpenInstalledProductModal, handleDeleteInstalledProduct]
	);

	if (!productsGroupedByCategory || productsGroupedByCategory.length === 0) {
		return null;
	}

	return (
		<>
			<div className="device-detection-categories-list">
				{productsGroupedByCategory.map((productTypeGroup, productTypeIndex) => {
					if (productTypeGroup.products.length === 0) return null;

					const categoryKey = `${productTypeGroup.product_type?.name}-${productTypeIndex}`;
					const isCategoryExpanded = expandedCategories[categoryKey] ?? true;

					return (
						<Card
							key={`published-category-${productTypeGroup.product_type?.name}-${productTypeIndex}`}
							className="hdd-summary-card"
						>
							{/* ── Category header with expand/collapse ── */}
							<div className="hdd-section-header">
								<img
									src={
										isCategoryExpanded
											? `${process.env.PUBLIC_URL}/assets/images/icons/chevron-up-gray.svg`
											: `${process.env.PUBLIC_URL}/assets/images/icons/chevron-down-gray.svg`
									}
									alt={isCategoryExpanded ? "collapse" : "expand"}
									className="hdd-expand-icon"
									onClick={() => toggleCategory(categoryKey)}
								/>
								<span className="hdd-section-header-title">
									{`${productTypeGroup.product_type.label} (${productTypeGroup.totalProducts})`}
								</span>
							</div>

							<div className="hdd-expandable">
								<p className="hdd-description">{"The following devices were installed as part of this project."}</p>

								{/* ── Collapsible category content ── */}
								{isCategoryExpanded && (
									<>
										{/* ── Devices in this category ── */}
										{productTypeGroup.products.map((product, productIndex) => {
											const productImgUri =
												product.photo_link ?? `${process.env.PUBLIC_URL}/assets/images/icons/sidebar-products.svg`;

											const productKey = `${categoryKey}-product-${product.id || productIndex}`;
											const isProductExpanded = expandedProducts[productKey] ?? true;

											return (
												<div key={`product-${product.id}-${productIndex}`} className="hdd-device-container">
													{/* Device info row with expand/collapse */}
													<div className="hdd-device-header-row">
														<div className="hdd-device-header-arrow">
															<img
																src={
																	isProductExpanded
																		? `${process.env.PUBLIC_URL}/assets/images/icons/chevron-up-gray.svg`
																		: `${process.env.PUBLIC_URL}/assets/images/icons/chevron-down-gray.svg`
																}
																alt={isProductExpanded ? "collapse" : "expand"}
																className="hdd-expand-icon"
																onClick={e => {
																	e.stopPropagation();
																	toggleProduct(productKey);
																}}
															/>
														</div>
														<img
															src={productImgUri}
															alt="Device icon"
															className={
																"device-detection-device-header-img" + (product.photo_link ? "" : " placeholder")
															}
														/>
														<div
															className="hdd-device-header-column"
															onClick={() => toggleProduct(productKey)}
															style={{ cursor: "pointer", flex: 1 }}
														>
															<div className="hdd-device-row">
																<span className="hdd-device-name">
																	{`${product.name} (${product.installed_products?.length || 0})`}
																</span>
															</div>

															<span className="hdd-device-matched-label">
																{`Product ID:`}
																<span className="hdd-device-matched-label id">{product.id}</span>
															</span>
														</div>

														{!readOnly && (
															<div className="hdd-device-header-right">
																<img
																	alt="edit-attachment-icon"
																	src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
																	className="selected-file-icon"
																	onClick={e => {
																		e.stopPropagation();
																		handleOpenProductModal(product);
																	}}
																/>
																<RemovePopconfirm
																	title="Delete Product"
																	description={<span>Are you sure you want to delete this product?</span>}
																	handleDeleteClick={() => handleDeleteProduct(product.id)}
																/>
															</div>
														)}
													</div>

													{product.description && (
														<div className="hdd-body-container">
															<p className="hdd-description">{product.description}</p>
														</div>
													)}

													{/* Collapsible product content */}
													{isProductExpanded && (
														<div className="hdd-body-container">
															{/* Installations table */}
															{product.installed_products && product.installed_products.length > 0 && (
																<div className="hdd-installations-table">
																	<Table
																		dataSource={product.installed_products as Installed_Products[]}
																		columns={installationsTableColumns}
																		pagination={false}
																		rowKey={r => r.id}
																		size="small"
																		expandable={{
																			expandedRowRender: record => (
																				<div className="hdd-installation-expandable">
																					{record.product_notes && (
																						<div>
																							<span className="hdd-device-matched-label">Notes:</span>
																							<p className="hdd-installation-note">{record.product_notes}</p>
																						</div>
																					)}
																					{record.installed_product_attachments &&
																						record.installed_product_attachments.length > 0 && (
																							<div>
																								<span className="hdd-device-matched-label">
																									Resources ({record.installed_product_attachments.length}):
																								</span>
																								{record.installed_product_attachments.map((file, index) => {
																									const FileIcon = getFileIcon(file.mime_type || "document");
																									return (
																										<div
																											key={`${record.id}-${file.name}-${index}`}
																											className="hdd-installation-document"
																											onClick={() => handleOnClickAttachment(file)}
																										>
																											<FileIcon className="hdd-installation-document-icon" />
																											<span className="hdd-installation-document-label">
																												{file.name}
																											</span>
																										</div>
																									);
																								})}
																							</div>
																						)}
																				</div>
																			),
																			rowExpandable: record =>
																				record.product_notes ||
																				(record.installed_product_attachments &&
																					record.installed_product_attachments.length > 0),
																			expandIcon: ({ expanded, onExpand, record }) =>
																				expanded ? (
																					<img
																						src={process.env.PUBLIC_URL + "/assets/images/icons/chevron-up-gray.svg"}
																						alt="chevron up icon"
																						onClick={e => onExpand(record, e)}
																						className="hdd-expand-icon"
																					/>
																				) : (
																					<img
																						src={process.env.PUBLIC_URL + "/assets/images/icons/chevron-down-gray.svg"}
																						alt="chevron down icon"
																						onClick={e => onExpand(record, e)}
																						className="hdd-expand-icon"
																					/>
																				)
																		}}
																	/>
																</div>
															)}
														</div>
													)}
												</div>
											);
										})}
									</>
								)}
							</div>
						</Card>
					);
				})}
			</div>

			{/* ── Modals ── */}
			<NewProductModal
				open={productModalOpen}
				onCancel={handleCloseProductModal}
				onSuccess={handleCloseProductModal}
				existingProduct={selectedProduct}
			/>

			<NewInstalledProductModal
				open={installedProductModalOpen}
				onCancel={handleCloseInstalledProductModal}
				onSuccess={handleCloseInstalledProductModal}
				property={handover.property as Properties}
				existingInstalledProduct={selectedInstalledProduct}
			/>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
				filesLimit={20}
			/>

			<MediaPlayer
				onCancel={() => setFileViewer({ type: null, src: "" })}
				src={fileViewer.src}
				open={!!fileViewer.src && fileViewer.type !== null}
				mediaType={fileViewer.type}
			/>
		</>
	);
};

export default HandoverPublishedDevices;
